/**
 * Builds help-assistant knowledge + route index from role manual configs.
 * Keep manuals as source of truth; this file only transforms them.
 */
import { OWNER_MANUAL_SECTIONS } from "./ownerManualContent.js";
import { SUPER_ADMIN_MANUAL_SECTIONS } from "./superAdminManualContent.js";
import { FRONT_DESK_MANUAL_SECTIONS } from "./frontDeskManualContent.js";
import { TREATMENT_COORDINATOR_MANUAL_SECTIONS } from "./treatmentCoordinatorManualContent.js";
import { AGENT_MANUAL_SECTIONS } from "./agentManualContent.js";
import { TEAM_LEADER_MANUAL_SECTIONS } from "./teamLeaderManualContent.js";

const ROLE_LABELS = {
  super_admin: "Super Admin",
  owner: "Owner",
  front_desk: "Front Desk",
  treatment_coordinator: "Treatment Coordinator",
  agent: "Agent",
  team_leader: "Team Leader",
};

const ROLE_MANUAL_HREF = {
  super_admin: "/admin/manual",
  owner: "/owner/manual",
  front_desk: "/front-desk/manual",
  treatment_coordinator: "/treatment-coordinator/manual",
  agent: "/agent/manual",
  team_leader: "/team-leader/manual",
};

const ROLE_SECTIONS = {
  super_admin: SUPER_ADMIN_MANUAL_SECTIONS,
  owner: OWNER_MANUAL_SECTIONS,
  front_desk: FRONT_DESK_MANUAL_SECTIONS,
  treatment_coordinator: TREATMENT_COORDINATOR_MANUAL_SECTIONS,
  agent: AGENT_MANUAL_SECTIONS,
  team_leader: TEAM_LEADER_MANUAL_SECTIONS,
};

/** App modules not yet in a role manual — merged into the global KB. */
const SUPPLEMENT_MODULES = [
  {
    menuLabel: "Consultation detail",
    route: "/consultations/:id",
    keywords: [
      "consultation detail",
      "transcript",
      "soap note",
      "export soap",
      "approve",
      "recording playback",
      "single consultation",
    ],
    roles: ["owner", "treatment_coordinator", "front_desk"],
    summary:
      "Single consultation view: transcript, SOAP note editor, recording playback, approve/save SOAP, and Export SOAP Note (.txt).",
    howToUse: [
      "From [[Consultations|/consultations]], click a row to open /consultations/:id.",
      "Review transcript and generated SOAP; edit and save or approve as your workflow defines.",
      "Click Export SOAP Note to download a .txt file for one visit.",
      "For bulk Excel export, use Download SOAP on the consultations list page.",
    ],
    faqs: [
      {
        question: "How do I export one SOAP note?",
        answer:
          "Open the consultation at /consultations/:id → Export SOAP Note (.txt). Bulk Excel: [[Consultations|/consultations]] → Download SOAP.",
      },
    ],
  },
  {
    menuLabel: "Conversation detail",
    route: "/conversations/:id",
    keywords: ["conversation detail", "sms thread", "reply", "message history"],
    roles: ["owner", "treatment_coordinator", "front_desk", "agent"],
    summary:
      "Single AI follow-up SMS thread: read message history and send manual replies when needed.",
    howToUse: [
      "From [[Conversations|/conversations]], click a thread to open /conversations/:id.",
      "Read the conversation history; reply using approved templates if allowed.",
    ],
    faqs: [
      {
        question: "How do I reply to a patient in Conversations?",
        answer:
          "Open the thread at /conversations/:id and use the reply box. This is for AI follow-up threads, not agent Lead Queue SMS.",
      },
    ],
  },
];

function roleLabelsFor(roles) {
  return (roles || []).map((r) => ROLE_LABELS[r] || r);
}

function stripMarkdown(text) {
  return String(text)
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .trim();
}

function defaultAccessLevel(role, section) {
  if (section.accessLevel) return section.accessLevel;
  if (role === "super_admin") return "manage";
  return "full";
}

function buildFaqs(section) {
  const manualFaqs = (section.faqs || []).map((faq) => ({
    question: faq.question,
    answer: stripMarkdown(faq.answer),
  }));

  if (section.openPage === false) {
    return manualFaqs;
  }

  return [
    ...manualFaqs,
    ...(section.keywords || []).slice(0, 5).map((kw) => ({
      question: `How do I use ${kw}?`,
      answer: `${section.summary} Open [[${section.menuLabel}|${section.route}]] to get started.`,
    })),
  ];
}

function buildCombinedDescription(mod) {
  const managers = roleLabelsFor(mod.manage_roles || []);
  const viewers = roleLabelsFor(mod.view_only_roles || []);
  if (managers.length && viewers.length) {
    return `${mod.module_name} (${mod.route_path}): ${managers.join(", ")} can create/edit/manage; ${viewers.join(", ")} can view only.`;
  }
  if (managers.length && !viewers.length) {
    return `${mod.description} Managed by: ${managers.join(", ")}.`;
  }
  return mod.description;
}

function mergeFaqs(existing, incoming) {
  const seen = new Set();
  const out = [];
  for (const faq of [...(existing || []), ...(incoming || [])]) {
    const key = String(faq.question || "").toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(faq);
  }
  return out;
}

function sectionToModule(section, role) {
  const access = defaultAccessLevel(role, section);
  const workflow = (section.howToUse || []).map(stripMarkdown);
  const mod = {
    module_name: section.menuLabel,
    route_path: section.route,
    description: section.summary,
    keywords: [...(section.keywords || [])],
    roles: [role],
    role_labels: roleLabelsFor([role]),
    manage_roles: access === "manage" || access === "full" ? [role] : [],
    view_only_roles: access === "view" ? [role] : [],
    role_capabilities: {
      [role]: {
        access,
        summary: section.summary,
        workflow_steps: workflow,
      },
    },
    actions: [
      {
        action_name: `Use ${section.menuLabel}`,
        action_purpose: section.summary,
        workflow_steps: workflow,
      },
    ],
    common_questions: buildFaqs(section),
  };
  mod.description = buildCombinedDescription(mod);
  return mod;
}

function mergeSectionIntoModule(existing, section, role) {
  if (!existing.roles.includes(role)) {
    existing.roles.push(role);
    existing.role_labels = roleLabelsFor(existing.roles);
  }

  const access = defaultAccessLevel(role, section);
  const workflow = (section.howToUse || []).map(stripMarkdown);

  if (!existing.role_capabilities) existing.role_capabilities = {};
  existing.role_capabilities[role] = {
    access,
    summary: section.summary,
    workflow_steps: workflow,
  };

  if (!existing.manage_roles) existing.manage_roles = [];
  if (!existing.view_only_roles) existing.view_only_roles = [];

  if (access === "manage" || access === "full") {
    if (!existing.manage_roles.includes(role)) existing.manage_roles.push(role);
  }
  if (access === "view") {
    if (!existing.view_only_roles.includes(role)) existing.view_only_roles.push(role);
  }

  for (const kw of section.keywords || []) {
    if (!existing.keywords.includes(kw)) existing.keywords.push(kw);
  }

  existing.common_questions = mergeFaqs(existing.common_questions, buildFaqs(section));
  existing.description = buildCombinedDescription(existing);
}

/** Merge all role manuals; dedupe by route with per-role access (view vs manage). */
export function buildAllModules() {
  const byRoute = new Map();
  const roleOrder = [
    "owner",
    "super_admin",
    "front_desk",
    "treatment_coordinator",
    "agent",
    "team_leader",
  ];

  for (const role of roleOrder) {
    for (const section of ROLE_SECTIONS[role] || []) {
      const existing = byRoute.get(section.route);
      if (existing) {
        mergeSectionIntoModule(existing, section, role);
        continue;
      }
      byRoute.set(section.route, sectionToModule(section, role));
    }
  }

  for (const sup of SUPPLEMENT_MODULES) {
    const section = {
      menuLabel: sup.menuLabel,
      route: sup.route,
      keywords: sup.keywords,
      summary: sup.summary,
      howToUse: sup.howToUse,
      faqs: sup.faqs,
      accessLevel: sup.accessLevel,
    };
    const existing = byRoute.get(sup.route);
    if (existing) {
      for (const role of sup.roles || []) {
        mergeSectionIntoModule(existing, section, role);
      }
      continue;
    }
    byRoute.set(sup.route, sectionToModule(section, sup.roles[0]));
    for (let i = 1; i < (sup.roles || []).length; i++) {
      mergeSectionIntoModule(byRoute.get(sup.route), section, sup.roles[i]);
    }
  }

  return Array.from(byRoute.values()).sort((a, b) =>
    a.route_path.localeCompare(b.route_path),
  );
}

export function buildHelpPromptContext(role) {
  const modules = buildAllModules();

  return {
    version: "1.0.0",
    system: "Chairside — AI dental practice management platform",
    role_catalog: Object.entries(ROLE_LABELS).map(([id, label]) => ({
      role: id,
      label,
      manual_href: ROLE_MANUAL_HREF[id],
    })),
    assistant_constraints: {
      source_of_truth:
        "Answer using modules in this knowledge base. Every claim must map to a documented route or workflow step.",
      fallback_response:
        "I don't have specific information about that in Chairside. Try rephrasing, or open [[Support Tickets|/tickets]] to contact your team.",
      cross_role_policy:
        "The knowledge base includes ALL app modules for every role. Check manage_roles vs view_only_roles: only manage_roles can create/edit/delete. If the user asks who can do something, name manage_roles explicitly. If the user's role is view-only or lacks access, say so clearly and explain which role manages it.",
      permission_examples: [
        "Locations (/settings/locations): Super Admin creates/edits; Owner views only.",
        "Treatments (/treatments): Owner manages catalog.",
        "Organizations (/admin/organizations): Super Admin only.",
        "Front Desk calling: [[Dashboard|/dashboard]] Call List + Follow-Up Tasks Call buttons — NOT [[Call History|/call-history]] (review only) and NOT [[Lead Queue|/agent/lead-queue]] (agents).",
      ],
      navigation_format:
        "Use clickable links [[Label|/path]] for every page mentioned. Paths must exist in NAVIGABLE_ROUTES.",
      response_style:
        "Short, clear, user-friendly (2–5 sentences). Numbered steps from workflow_steps when explaining how-to. Plain text except [[Label|/path]] links.",
      do_not_assume: [
        "Do not invent menu items, routes, or buttons not in the knowledge base.",
        "Do not grant create/edit permissions to view-only roles.",
        "If the user asks about something not documented, use fallback_response and suggest [[Support Tickets|/tickets]].",
      ],
    },
    auth_and_session: {
      login: "/login",
      logout: "Use avatar menu → Sign out",
      profile: "/settings/profile",
      location_switcher: "Header location dropdown — filters data by office",
    },
    modules,
    all_modules_count: modules.length,
    user_role: role || "unknown",
    user_role_label: ROLE_LABELS[role] || role || "unknown",
    user_accessible_modules: modules
      .filter((m) => (m.roles || []).includes(role))
      .map((m) => m.module_name),
    manual_href: ROLE_MANUAL_HREF[role] || "/owner/manual",
  };
}

export function buildHelpRouteIndex() {
  const index = new Map();
  for (const mod of buildAllModules()) {
    index.set(mod.route_path, {
      path: mod.route_path,
      label: mod.module_name,
      description: mod.description,
    });
  }
  index.set("/tickets", {
    path: "/tickets",
    label: "Support Tickets",
    description: "Open a support ticket for bugs or help requests.",
  });
  return index;
}

export function getHelpRoutesList() {
  return Array.from(buildHelpRouteIndex().values()).sort((a, b) =>
    a.label.localeCompare(b.label),
  );
}

export function getHelpRoute(path) {
  const normalized = String(path || "")
    .trim()
    .split("?")[0]
    .split("#")[0];
  return buildHelpRouteIndex().get(normalized);
}

export function getManualHrefForRole(role) {
  return ROLE_MANUAL_HREF[role] || "/owner/manual";
}

export function getRoleLabels() {
  return { ...ROLE_LABELS };
}

export { ROLE_LABELS, roleLabelsFor };
